import React, { ReactElement, useState, useEffect } from 'react'
import {
  Box,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Chip,
} from '@mui/material'
import { useForm } from '@tanstack/react-form'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  assignTemplate,
  updateTemplateAssignment,
  fetchTemplate,
  fetchEventCompliances,
} from 'src/v3/content-pipeline/event-pipeline.slice'
import {
  fetchEventTemplates,
  selectEventTemplates,
  selectEventTemplatesStatus,
} from 'src/v3/events/events.slice'
import { ContentComplianceStatus } from 'src/v3/core/types'

type TemplateManagementProps = {
  eventID: string
  template?: {
    id: string
    linkedServiceID?: string
    name: string
    statement1: string
    statement2: string
    createdTime?: string
    updatedTime?: string
  }
  templateStatus?: string
  complianceStatus?: ContentComplianceStatus
  onUpdate?: () => void // Callback to trigger refetch in parent
}

export default ({
  eventID,
  template,
  complianceStatus,
  onUpdate,
}: TemplateManagementProps): ReactElement => {
  let dispatch = useAppDispatch()
  let [isEditing, setIsEditing] = useState(false)
  let [loading, setLoading] = useState(false)

  let availableTemplates = useAppSelector(selectEventTemplates)
  let availableTemplatesStatus = useAppSelector(selectEventTemplatesStatus)

  let hasAssignedTemplate = !!template

  let form = useForm({
    defaultValues: {
      templateID: template?.id || '',
    },
    onSubmit: async ({ value }) => {
      setLoading(true)
      try {
        if (!value.templateID) {
          setLoading(false)
          return
        }

        if (hasAssignedTemplate) {
          // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
          await dispatch(
            updateTemplateAssignment({
              eventID,
              templateID: value.templateID,
            }),
          )
        } else {
          // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
          await dispatch(
            assignTemplate({
              eventID,
              templateID: value.templateID,
            }),
          )
        }

        // Refetch template and compliance statuses
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchTemplate(eventID))
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchEventCompliances())
        setIsEditing(false)

        // Trigger parent refetch if callback provided
        if (onUpdate) {
          onUpdate()
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to assign template:', error)
      } finally {
        setLoading(false)
      }
    },
  })

  let startEditing = () => {
    form.setFieldValue('templateID', template?.id || '')
    setIsEditing(true)
  }

  let cancelEditing = () => {
    setIsEditing(false)
    form.reset()
  }

  // Fetch available templates when component mounts
  useEffect(() => {
    if (availableTemplatesStatus === 'uninitialized') {
      dispatch(fetchEventTemplates())
    }
  }, [availableTemplatesStatus, dispatch])

  if (!isEditing && !hasAssignedTemplate) {
    return (
      <Box
        sx={{
          p: 3,
          bgcolor: 'background.paperDark',
          borderRadius: 1,
          textAlign: 'center',
        }}
      >
        <Typography variant="h6" sx={{ mb: 1 }}>
          No Template Assigned
        </Typography>
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{
            mb: 2,
          }}
        >
          Assign a certificate template to define how your course completion
          certificates will appear.
        </Typography>
        <Button
          variant="contained"
          onClick={startEditing}
          disabled={
            availableTemplatesStatus !== 'fulfilled' ||
            availableTemplates.length === 0
          }
          aria-label="Assign template"
        >
          {availableTemplatesStatus === 'pending'
            ? 'Loading Templates...'
            : 'Assign Template'}
        </Button>
      </Box>
    )
  }

  if (!isEditing && hasAssignedTemplate && template) {
    return (
      <Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6" component="h4">
            Certificate Template
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={startEditing}
            disabled={availableTemplatesStatus !== 'fulfilled'}
            aria-label="Change template"
          >
            Change Template
          </Button>
        </Box>
        {/* Template Compliance Status */}
        {complianceStatus && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
              p: 2,
              bgcolor: 'background.paperDark',
              borderRadius: 1,
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 'medium',
              }}
            >
              Template Status
            </Typography>
            <Chip
              label={
                complianceStatus === 'approved'
                  ? 'Approved'
                  : complianceStatus === 'rejected'
                    ? 'Rejected'
                    : complianceStatus === 'updated'
                      ? 'Updated - Pending Review'
                      : complianceStatus === 'unapproved'
                        ? 'Unapproved'
                        : 'Pending Review'
              }
              variant="outlined"
              sx={{
                color:
                  complianceStatus === 'approved'
                    ? 'success'
                    : complianceStatus === 'rejected' ||
                        complianceStatus === 'unapproved'
                      ? 'error'
                      : 'primary',
              }}
            />
          </Box>
        )}
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {template.name}
          </Typography>

          <Typography
            color="text.secondary"
            variant="subtitle2"
            sx={{
              mb: 1,
            }}
          >
            Accrediting Agency 1 Statement:
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
            {template.statement1}
          </Typography>

          <Typography
            color="text.secondary"
            variant="subtitle2"
            sx={{
              mb: 1,
            }}
          >
            Accrediting Agency 2 Statement:
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {template.statement2}
          </Typography>

          <Typography
            color="text.secondary"
            variant="caption"
            sx={{
              mt: 2,
              display: 'block',
              fontStyle: 'italic',
            }}
          >
            Note: $HOUR in statements will be replaced with the course length on
            certificates.
          </Typography>
        </Paper>
      </Box>
    )
  }

  // Editing mode
  return (
    <Box>
      <Typography variant="h6" component="h4" sx={{ mb: 2 }}>
        {hasAssignedTemplate
          ? 'Change Certificate Template'
          : 'Assign Certificate Template'}
      </Typography>

      <form.Field
        name="templateID"
        children={(field) => (
          <>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Select Template</InputLabel>
              <Select
                value={field.state.value}
                label="Select Template"
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                disabled={loading || availableTemplatesStatus === 'pending'}
                sx={{ bgcolor: 'background.paperDark' }}
              >
                <MenuItem value="">
                  <em>Select a template</em>
                </MenuItem>
                {availableTemplates.map((availableTemplate) => (
                  <MenuItem
                    key={availableTemplate.id}
                    value={availableTemplate.id}
                  >
                    {availableTemplate.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={() => form.handleSubmit()}
                disabled={loading || !field.state.value}
                aria-label="Save template assignment"
              >
                {loading
                  ? 'Saving...'
                  : hasAssignedTemplate
                    ? 'Update Template'
                    : 'Assign Template'}
              </Button>
              <Button
                variant="outlined"
                onClick={cancelEditing}
                disabled={loading}
                aria-label="Cancel template assignment"
              >
                Cancel
              </Button>
            </Box>
          </>
        )}
      />
    </Box>
  )
}
